// EXPERIMENT: psf_promote_or_share_qr_code_experiment — remove with experiment cleanup
import type React from 'react';
import { useCallback, useId } from 'react';

import { Button } from '@change/ds-core-ui/Button';
import { Text } from '@change/ds-core-ui/Text';
import { cn } from '@change/ds-utils';

import { useI18n } from '@change-corgi/core-react/i18n';

import type { QrCodePoster, ShareFileMethod } from '#src/app/pages/psf/promoteOrShare/shared/hooks';
import { useShareFile } from '#src/app/pages/psf/promoteOrShare/shared/hooks';
import type { FlyerPreviewImage } from '#src/app/shared/hooks/flyer';
import { useFlyerA } from '#src/app/shared/hooks/flyer';
import { useTrackView } from '#src/app/shared/hooks/tracking';

import { StepBody } from '../StepBody';

/** The print medium's QR type, so poster scans are told apart from screen scans. */
const QR_CODE_TYPE = 'WEB_PSF_PROMOTE_OR_SHARE_FLYER_A';

/** Letter-size paper, which the Flyer A PDF is. */
const PREVIEW_CLASSNAME = 'aspect-[17/22] max-h-[45dvh] w-auto max-w-full rounded-md';

/**
 * The static Flyer A preview, or a decorative placeholder in its place once both the locale image and
 * the non-locale fallback failed. Keyed by the image, so the fallback loads in a fresh element.
 */
const PosterPreview: React.FC<{ previewImage: FlyerPreviewImage | undefined; onError: () => void }> = ({
	previewImage,
	onError,
}) => {
	if (!previewImage) {
		return (
			<div
				aria-hidden
				className={cn('bg-fill-weaker h-[45dvh]', PREVIEW_CLASSNAME)}
				data-testid="poster-view-placeholder"
			/>
		);
	}
	return (
		<picture key={previewImage.src}>
			{previewImage.webpSrcSet && <source type="image/webp" srcSet={previewImage.webpSrcSet} />}
			<img
				src={previewImage.src}
				srcSet={previewImage.srcSet}
				alt={previewImage.alt}
				onError={onError}
				className={cn('border-stroke-weaker border', PREVIEW_CLASSNAME)}
				data-testid="poster-view-preview"
			/>
		</picture>
	);
};

/**
 * EXPERIMENT: psf_promote_or_share_qr_code_experiment.
 * The share slide-up's poster view body (`variant_2`): the Flyer A preview and Print. Print shares only
 * from a tap: a PDF that lands after the view unmounted is kept for the next poster view in the app
 * session. The slide-up owns the header.
 */
export const PosterView: React.FC<PosterViewProps> = ({
	petitionId,
	petitionTitle,
	getTrackingData,
	trackPrinted,
	onShared,
}) => {
	const { translate } = useI18n();
	// The header has no title, so this hidden name tells screen readers the view changed.
	const nameId = useId();
	const {
		data: { status, file, previewImage, shareId },
		actions: { trackShare, handlePreviewImageError },
	} = useFlyerA({
		petitionId,
		petitionTitle,
		qrCodeType: QR_CODE_TYPE,
		getTrackingData,
		feature: 'psf_promote_or_share_qr_code_experiment',
	});
	const { shareFile } = useShareFile({ source: 'qr_poster', onShared });
	useTrackView('psf_qr_poster_view', getTrackingData());

	// Recorded at the tap, whatever the share's outcome. A tap that the double-tap guard blocks (`null`)
	// shares nothing and records nothing, so a double tap records once. `share_petition` carries the
	// poster code's share id, which recruits from a printed poster are joined on.
	const handlePrint = useCallback(() => {
		if (!file) return;
		if (!shareFile(file)) return;
		trackShare();
		trackPrinted(shareId);
	}, [file, shareFile, trackShare, trackPrinted, shareId]);

	const isPreparing = status === 'preparing';

	return (
		<StepBody testId="poster-view" labelledBy={nameId}>
			<p id={nameId} className="sr-only">
				{translate('corgi.psf.promote_or_share.share_slide_up.qr_code.print_poster')}
			</p>
			<PosterPreview previewImage={previewImage} onError={handlePreviewImageError} />
			{status === 'failed' && (
				<Text as="p" role="alert" size="small" className="text-typography-weak text-center">
					{translate('corgi.psf.promote_or_share.share_slide_up.poster.error')}
				</Text>
			)}
			<Button
				variant="primary"
				className="w-full"
				loading={isPreparing}
				disabled={status === 'failed'}
				aria-label={isPreparing ? translate('corgi.psf.promote_or_share.share_slide_up.poster.preparing') : undefined}
				onClick={handlePrint}
				data-testid="poster-view-print-button"
			>
				{translate('corgi.psf.promote_or_share.share_slide_up.poster.print')}
			</Button>
		</StepBody>
	);
};

type PosterViewProps = Pick<QrCodePoster, 'petitionId' | 'getTrackingData' | 'trackPrinted'> & {
	/** The petition title printed on the poster. */
	petitionTitle: string;
	/** Called once Print delivered the PDF. The slide-up redirects to the share page. */
	onShared?: (method: ShareFileMethod) => void;
};
